<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { formatNumber } from '@/composables/useFormat'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Inventory',
  },
})

const ability = useAbility()
const { loading, errors, request } = useApiRequest()

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------
const units = ref([])
const search = ref('')
const isFetching = ref(false)

const dialog = ref(false)
const dialogMode = ref('create')
const dialogLoading = ref(false)
const deleteDialog = ref(false)
const selectedItem = ref(null)

const defaultForm = () => ({ name: '', symbol: '', is_derived: false, base_unit_id: null, conversion_factor: null })
const form = ref(defaultForm())
const formRef = ref()

const headers = [
  { title: 'Nama Satuan', key: 'name' },
  { title: 'Simbol', key: 'symbol', width: '110px' },
  { title: 'Konversi', key: 'conversion', sortable: false, width: '220px' },
  { title: 'Dibuat', key: 'created_at', width: '160px' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px', align: 'end' },
]

// Satuan yang boleh dipilih sebagai "Satuan Dasar" — cuma satuan dasar ASLI
// (base_unit_id null), dan kalau mode edit, tidak boleh pilih diri sendiri.
// Sinkron dengan validasi anti-rantai di UnitRequest::withValidator().
const baseUnitOptions = computed(() =>
  units.value.filter(u => !u.base_unit_id && u.id !== selectedItem.value?.id),
)

// Quick-fill satuan umum. derivedFromSymbol+factor opsional: kalau satuan
// dasarnya (mis. "g" untuk "kg") sudah ada di tenant ini, auto-link jadi
// satuan turunan sekalian — user tidak perlu isi manual utk pasangan metrik
// yang umum dipakai (kg/g, l/ml).
const commonUnits = [
  { name: 'Gram',      symbol: 'g' },
  { name: 'Kilogram',  symbol: 'kg', derivedFromSymbol: 'g',  factor: 1000 },
  { name: 'Mililiter', symbol: 'ml' },
  { name: 'Liter',     symbol: 'l',  derivedFromSymbol: 'ml', factor: 1000 },
  { name: 'Porsi',     symbol: 'pcs' },
  { name: 'Lembar',    symbol: 'lbr' },
  { name: 'Botol',     symbol: 'btl' },
  { name: 'Sachet',    symbol: 'sct' },
]

// -----------------------------------------------------------------------
// Fetch — ✅ FIX: response?.data ?? response
// -----------------------------------------------------------------------
const fetchUnits = async () => {
  isFetching.value = true
  try {
    const params = {}
    if (search.value) params.search = search.value

    const response = await useApi('/api/v1/units', { query: params }).get()
    const data = response?.data ?? response

    units.value = Array.isArray(data) ? data : (data?.data ?? [])
  } finally {
    isFetching.value = false
  }
}

// -----------------------------------------------------------------------
// Dialog helpers
// -----------------------------------------------------------------------
const openCreate = () => {
  dialogMode.value = 'create'
  form.value = defaultForm()
  formRef.value?.reset()
  dialog.value = true
}

const openEdit = (item) => {
  dialogMode.value = 'edit'
  selectedItem.value = item
  form.value = {
    name: item.name,
    symbol: item.symbol,
    is_derived: !!item.base_unit_id,
    base_unit_id: item.base_unit_id,
    conversion_factor: item.conversion_factor,
  }
  dialog.value = true
}

const confirmDelete = (item) => {
  selectedItem.value = item
  deleteDialog.value = true
}

const fillCommon = (unit) => {
  form.value.name   = unit.name
  form.value.symbol = unit.symbol

  const baseUnit = unit.derivedFromSymbol
    ? units.value.find(u => u.symbol === unit.derivedFromSymbol && !u.base_unit_id)
    : null

  if (baseUnit) {
    form.value.is_derived = true
    form.value.base_unit_id = baseUnit.id
    form.value.conversion_factor = unit.factor
  } else {
    form.value.is_derived = false
    form.value.base_unit_id = null
    form.value.conversion_factor = null
  }
}

// -----------------------------------------------------------------------
// CRUD — ✅ FIX: response parsing
// -----------------------------------------------------------------------
const submitForm = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  dialogLoading.value = true

  // is_derived cuma toggle tampilan FE, bukan field backend — kalau off,
  // pastikan base_unit_id/conversion_factor ikut null (bukan sisa dari
  // toggle sebelumnya).
  const payload = {
    name: form.value.name,
    symbol: form.value.symbol,
    base_unit_id: form.value.is_derived ? form.value.base_unit_id : null,
    conversion_factor: form.value.is_derived ? form.value.conversion_factor : null,
  }

  const res = await request(async () => {
    if (dialogMode.value === 'create') {
      return await useApi('/api/v1/units').post({ body: payload })
    } else {
      return await useApi(`/api/v1/units/${selectedItem.value.id}`).put({ body: payload })
    }
  })

  dialogLoading.value = false

  if (res) {
    dialog.value = false
    await fetchUnits()
  }
}

const deleteUnit = async () => {
  const res = await request(() =>
    useApi(`/api/v1/units/${selectedItem.value.id}`).delete()
  )
  if (res !== null) {
    deleteDialog.value = false
    await fetchUnits()
  }
}

// -----------------------------------------------------------------------
// Format
// -----------------------------------------------------------------------
const formatDate = (val) => {
  if (!val) return '-'
  return new Date(val).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

// -----------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------
onMounted(fetchUnits)

let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(fetchUnits, 400)
})
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Satuan</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Kelola satuan ukuran untuk bahan baku (gram, ml, pcs, dll)
        </p>
      </VCol>
    </VRow>

    <VCard>
      <!-- Toolbar -->
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VTextField
          v-model="search"
          placeholder="Cari satuan..."
          prepend-inner-icon="ri-search-line"
          density="compact"
          hide-details
          clearable
          style="max-width: 280px"
        />
        <VSpacer />
        <VBtn
          v-if="ability.can('create', 'Inventory')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Satuan
        </VBtn>
      </VCardText>

      <VDivider />

      <!-- Table -->
      <VDataTable
        :headers="headers"
        :items="units"
        :loading="isFetching"
        hover
        no-data-text="Belum ada satuan"
      >
        <template #item.symbol="{ item }">
          <VChip color="primary" variant="tonal" size="small">
            {{ item.symbol }}
          </VChip>
        </template>

        <template #item.conversion="{ item }">
          <span v-if="item.base_unit_id" class="text-body-2">
            1 {{ item.symbol }} = {{ formatNumber(item.conversion_factor) }} {{ item.base_unit?.symbol ?? '' }}
          </span>
          <VChip v-else size="small" variant="tonal" color="secondary">
            Satuan Dasar
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          <span class="text-medium-emphasis text-body-2">
            {{ formatDate(item.created_at) }}
          </span>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Inventory')"
            icon size="small" variant="text" color="primary"
            @click="openEdit(item)"
          >
            <VIcon icon="ri-edit-line" />
            <VTooltip activator="parent">Edit</VTooltip>
          </VBtn>
          <VBtn
            v-if="ability.can('delete', 'Inventory')"
            icon size="small" variant="text" color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip activator="parent">Hapus</VTooltip>
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-scales-2-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-3 mb-1">Belum ada satuan</p>
            <p class="text-body-2 text-medium-emphasis mb-4">
              Tambahkan satuan untuk bahan baku Anda (gram, ml, pcs, dll)
            </p>
            <VBtn
              v-if="ability.can('create', 'Inventory')"
              color="primary" variant="tonal"
              @click="openCreate"
            >
              Tambah Satuan Pertama
            </VBtn>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- ================================================================
         Dialog: Create / Edit
    ================================================================ -->
    <VDialog v-model="dialog" max-width="460" persistent>
      <VCard>
        <VCardTitle class="pa-4 pb-0 d-flex align-center justify-space-between">
          <span>{{ dialogMode === 'create' ? 'Tambah Satuan' : 'Edit Satuan' }}</span>
          <VBtn icon size="small" variant="text" @click="dialog = false">
            <VIcon icon="ri-close-line" />
          </VBtn>
        </VCardTitle>

        <VForm ref="formRef" @submit.prevent="submitForm">
          <VCardText class="pt-3">
            <!-- Quick-fill chips (hanya mode create) -->
            <div v-if="dialogMode === 'create'" class="mb-4">
              <p class="text-body-2 font-weight-medium mb-2">Satuan Umum</p>
              <div class="d-flex gap-2 flex-wrap">
                <VChip
                  v-for="u in commonUnits"
                  :key="u.symbol"
                  size="small"
                  variant="tonal"
                  color="primary"
                  class="cursor-pointer"
                  @click="fillCommon(u)"
                >
                  {{ u.symbol }} — {{ u.name }}
                </VChip>
              </div>
            </div>

            <VRow>
              <VCol cols="12" md="8">
                <VTextField
                  v-model="form.name"
                  label="Nama Satuan"
                  placeholder="Gram"
                  :rules="[requiredValidator]"
                  :error-messages="errors.name"
                  autofocus
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.symbol"
                  label="Simbol"
                  placeholder="g"
                  :rules="[requiredValidator]"
                  :error-messages="errors.symbol"
                  hint="Unik per toko"
                  persistent-hint
                />
              </VCol>

              <VCol cols="12">
                <VSwitch
                  v-model="form.is_derived"
                  label="Ini satuan turunan (konversi dari satuan lain)"
                  color="primary"
                  density="compact"
                  :disabled="!baseUnitOptions.length"
                  hide-details
                />
                <p v-if="!baseUnitOptions.length" class="text-caption text-medium-emphasis mt-1">
                  Belum ada satuan dasar lain untuk dijadikan acuan.
                </p>
              </VCol>

              <template v-if="form.is_derived">
                <VCol cols="12" sm="7">
                  <VSelect
                    v-model="form.base_unit_id"
                    :items="baseUnitOptions"
                    item-title="name"
                    item-value="id"
                    label="Satuan Dasar *"
                    placeholder="mis. Gram"
                    :rules="[requiredValidator]"
                    :error-messages="errors.base_unit_id"
                  />
                </VCol>
                <VCol cols="12" sm="5">
                  <VTextField
                    v-model.number="form.conversion_factor"
                    label="Faktor Konversi *"
                    type="number"
                    min="0"
                    step="any"
                    :rules="[requiredValidator, v => v > 0 || 'Harus lebih dari 0']"
                    :error-messages="errors.conversion_factor"
                  />
                </VCol>
                <VCol v-if="form.base_unit_id && form.conversion_factor" cols="12">
                  <VAlert type="info" variant="tonal" density="compact">
                    1 {{ form.symbol || '...' }} =
                    {{ formatNumber(form.conversion_factor) }}
                    {{ baseUnitOptions.find(u => u.id === form.base_unit_id)?.symbol }}
                  </VAlert>
                </VCol>
              </template>
            </VRow>
          </VCardText>

          <VDivider />

          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="dialog = false">Batal</VBtn>
            <VBtn type="submit" color="primary" :loading="dialogLoading">
              {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <!-- ================================================================
         Dialog: Delete
    ================================================================ -->
    <VDialog v-model="deleteDialog" max-width="440">
      <VCard>
        <VCardTitle class="text-h6 pa-4 pb-0">Hapus Satuan?</VCardTitle>
        <VCardText class="pa-4 pt-2">
          Hapus satuan
          <strong>{{ selectedItem?.name }} ({{ selectedItem?.symbol }})</strong>?
          Satuan yang masih digunakan bahan baku tidak bisa dihapus.
        </VCardText>
        <VCardActions class="pa-4 pt-0">
          <VSpacer />
          <VBtn variant="text" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="deleteUnit">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
